import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { 
  Search, Users, MapPin, Award, MessageCircle, CheckCircle, Filter
} from "lucide-react";

const MembersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [provinces, setProvinces] = useState([]);

  const ROLES = [
    { value: "member", label: "Member" },
    { value: "owner", label: "Practice Owner" },
    { value: "office_manager", label: "Office Manager" },
    { value: "myofunctional_therapist", label: "Myofunctional Therapist" }
  ];

  useEffect(() => {
    fetchProvinces();
    fetchMembers();
  }, [selectedProvince, selectedRole]);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error("Failed to fetch provinces");
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedProvince) params.append("province", selectedProvince);
      if (selectedRole) params.append("role", selectedRole);

      const response = await axios.get(`${API}/users/members?${params.toString()}`);
      setMembers(response.data);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(search.toLowerCase()) ||
    member.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleLabel = (role) => {
    return ROLES.find(r => r.value === role)?.label || "Member";
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="members-page">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <EditableText
            blockKey="members.title"
            fallback="Members Directory"
            as="h1"
            className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <EditableText
            blockKey="members.subtitle"
            fallback="Connect with verified dental hygienists across Canada"
            as="p"
            className="text-[#64748B] mt-1"
            multiline
          />
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-4 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="pl-12 bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#6B4E8A]"
                data-testid="search-members-input"
              />
            </div>
            <Select value={selectedProvince || "all"} onValueChange={(v) => setSelectedProvince(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-[#FAF7F2] border-[#E5DFEC]">
                <MapPin className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="All Provinces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Provinces</SelectItem>
                {provinces.map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedRole || "all"} onValueChange={(v) => setSelectedRole(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-[#FAF7F2] border-[#E5DFEC]">
                <Award className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Members Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-[#F0ECF7] rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-[#F0ECF7] rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-[#F0ECF7] rounded w-1/2"></div>
                  </div>
                </div>
                <div className="h-4 bg-[#F0ECF7] rounded w-full mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredMembers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member, idx) => (
              <motion.div
                key={member.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-[#E5DFEC] rounded-2xl p-6 card-hover"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center text-xl font-medium shrink-0">
                    {member.picture ? (
                      <img src={member.picture} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      member.name?.[0] || 'U'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-[#6B4E8A] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {member.name}
                      </h3>
                      <CheckCircle className="w-4 h-4 text-[#10B981] shrink-0" />
                    </div>
                    <p className="text-sm text-[#8DB5A0]">{getRoleLabel(member.role)}</p>
                  </div>
                </div>

                {member.province && (
                  <div className="flex items-center gap-2 text-sm text-[#64748B] mb-3">
                    <MapPin className="w-4 h-4" />
                    {provinces.find(p => p.code === member.province)?.name || member.province}
                  </div>
                )}

                {member.qualifications?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {member.qualifications.slice(0, 2).map((qual) => (
                      <span key={qual} className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                        {qual}
                      </span>
                    ))}
                    {member.qualifications.length > 2 && (
                      <span className="px-2 py-0.5 bg-[#F0ECF7] text-[#64748B] text-xs rounded-full">
                        +{member.qualifications.length - 2}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Link
                    to={`/profile/${member.user_id}`}
                    className="flex-1"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-[#E5DFEC] text-[#6B4E8A] hover:bg-[#F0ECF7] rounded-full text-sm"
                    >
                      View Profile
                    </Button>
                  </Link>
                  <Button
                    onClick={() => navigate(`/messages/${member.user_id}`)}
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-4"
                    data-testid={`message-${member.user_id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <Users className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No verified members found
            </h3>
            <p className="text-[#64748B]">
              {search || selectedProvince || selectedRole
                ? "Try adjusting your filters"
                : "Be the first to get verified!"}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MembersPage;
